/**
 * Authentication Context
 * Manages JWT tokens and user authentication state
 */

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { auth as authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // New D2L launches carry the token in the fragment (never sent in an HTTP
  // request or Referer). Still accept the old ?token= handoff for saved links.
  // /open-app uses an opaque #host/path?query fragment. An app's own &token=
  // parameter must never be mistaken for a dashboard sign-in.
  const incomingToken = (location.hash.startsWith('#token=')
    ? new URLSearchParams(location.hash.slice(1)).get('token') : null)
    || new URLSearchParams(location.search).get('token');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => incomingToken || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const currentToken = useRef(token);

  const loginWithToken = useCallback((newToken, userData = null) => {
    localStorage.setItem('token', newToken);
    setAuthError(null);
    // Receiving the same handoff twice must not discard a loaded profile or
    // leave loading=true without a new profile request.
    if (currentToken.current === newToken) return;
    currentToken.current = newToken;
    queryClient.clear();
    setToken(newToken);
    setUser(userData);
    setLoading(!userData);
  }, [queryClient]);

  const logout = useCallback(() => {
    currentToken.current = null;
    setUser(null);
    setToken(null);
    setLoading(false);
    setAuthError(null);
    localStorage.removeItem('token');
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    if (!incomingToken) return;
    loginWithToken(incomingToken);
    const query = new URLSearchParams(location.search);
    const fragment = new URLSearchParams(location.hash.slice(1));
    query.delete('token');
    const hadFragmentToken = location.hash.startsWith('#token=');
    fragment.delete('token');
    navigate({
      pathname: location.pathname,
      search: query.toString() ? `?${query}` : '',
      hash: hadFragmentToken ? (fragment.toString() ? `#${fragment}` : '') : location.hash,
    }, { replace: true });
    // No reload: LockDown Browser can refuse it. Authentication state itself
    // must consume the token before a protected route decides to show login.
  }, [incomingToken, location.pathname, location.search, location.hash, navigate, loginWithToken]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return undefined;
    }
    if (currentToken.current !== token) return undefined;
    localStorage.setItem('token', token);
    authAPI.getProfile(token).then((response) => {
      if (cancelled || currentToken.current !== token) return;
      setUser(response.user || response);
      setLoading(false);
    }).catch(() => {
      if (cancelled || currentToken.current !== token) return;
      logout();
      setAuthError('Your sign-in could not be completed. Return to D2L and open the lab link again.');
    });
    return () => { cancelled = true; };
  }, [token, logout]);

  useEffect(() => {
    const changed = (event) => {
      if (event.key !== 'token') return;
      if (event.newValue) loginWithToken(event.newValue);
      else logout();
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [loginWithToken, logout]);

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Password change failed' 
      };
    }
  };

  const value = {
    user,
    token,
    loading: loading || !!(incomingToken && incomingToken !== token),
    authError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isFaculty: user?.role === 'faculty' || user?.role === 'admin',
    isStudent: user?.role === 'student',
    loginWithToken,
    logout,
    changePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
